import {
    ClinicianCredential,
    DateFormats,
    DateType,
    IAvailabilitySchedule,
    IClient,
    IClientBooking,
    IClinician,
    IOfficeWindow,
    IOfficeWindowSchedule,
    ISchedule,
    OfficeMangerOfficeWindow,
    RecurrenceRule,
    SignatureData,
    TimesUsed,
    ConsentForm,
    STATES,
} from "intake_shared/src/types";
import { assignIn, cloneDeep, sortBy } from "lodash";
import { formatPhoneNumber, isPossiblePhoneNumber } from "react-phone-number-input";
import { v5 as uuidv5 } from "uuid";
import moment from "../utils/app_moment";
import { IAvailabilityWindow } from "./../../intake_shared/src/types";
import { orderBy, uniqBy } from "lodash";
import { SuggestTimeAndOffice } from "../types";
import { DepartmentCache } from "../store/cache/requested_service";
import {
    sendAppointmentConfirmedCareerAssessmentClientEmails,
    sendAppointmentConfirmedCHPInPersonClientEmails,
    sendAppointmentConfirmedCHPTeletherapyClientEmails,
    sendAppointmentConfirmedEmails,
    sendAppointmentConfirmedExternTherapyClientEmails,
    sendAppointmentConfirmedHTInPersonClientEmails,
    sendAppointmentConfirmedHTTeletherapyClientEmails,
    sendAppointmentConfirmedKAPClientEmails,
    sendAppointmentConfirmedNutritionCounselingClientEmails,
    sendAppointmentConfirmedPHInPersonClientEmails,
    sendAppointmentConfirmedPHTeletherapyClientEmails,
    sendAppointmentConfirmedPnpTherapyClientEmails,
    sendAppointmentConfirmedTestingClientEmails,
} from "./queue_email";
import { isSameString } from "intake_shared/src/util";
import { formatFeeCalculation } from "intake_shared/src/fees";

const NAMESPACE = "984a99cf-88c4-4183-ba95-96abd4e60fd3";
const MAX_APPT_DATE_DIFF_WEEKS = 4;

export function generate_uuid(seed: string) {
    const id = uuidv5(seed, NAMESPACE);
    return id;
}

export function hexToRgb(hex: string) {
    var bigint = parseInt(hex, 16);
    var r = (bigint >> 16) & 255;
    var g = (bigint >> 8) & 255;
    var b = bigint & 255;

    return r + "," + g + "," + b;
}

export function generateRandomColor(
    hashNumber?: number,
    numberOnly = false,
    makeRandom = true
) {
    const randomColor = Math.floor(
        (parseFloat(`0.${Math.abs(hashNumber || 0)}`) ||
            (makeRandom ? Math.random() : 0.5)) * 16777215
    ).toString(16);
    return `${numberOnly ? "" : "#"}${randomColor}`;
}

export function getHashNumber(sourceString: string): number {
    var hash = 0,
        i,
        chr;
    for (i = 0; i < sourceString.length; i++) {
        chr = sourceString.charCodeAt(i);
        hash = (hash << 5) - hash + chr;
        hash |= 0; // Convert to 32bit integer
    }
    return hash;
}

export function getStringFromScheduleDate(date?: string | Date): string {
    if (!date) {
        return "";
    }
    try {
        return (date as any).toUTCString();
    } catch {
        return date as string;
    }
}

export function getNumberOfWeeks(
    recurrence: string = RecurrenceRule.WEEKLY.toLowerCase()
): number {
    switch (recurrence.toLowerCase()) {
        case RecurrenceRule.BIWEEKLY.toLowerCase():
            return 2;
        case RecurrenceRule.WEEKLY.toLowerCase():
            return 1;
        case RecurrenceRule.TRIWEEKLY.toLowerCase():
            return 3;
        case RecurrenceRule.MONTHLY.toLowerCase():
            return 4;
        case RecurrenceRule.ONE_OFF.toLowerCase():
            return 0;
        case RecurrenceRule.TWO_MONTHS.toLowerCase():
            return 8;
        case RecurrenceRule.THREE_MONTHS.toLowerCase():
            return 12;
        default:
            return 1;
    }
}

export function getDayTimeFromScheduleDate(
    date?: string | Date,
    format = "h:mmA"
): string {
    const strDate = getStringFromScheduleDate(date);
    if (strDate) {
        return moment(strDate).format(format);
    }
    return "";
}

export function tConvert(time: string): string {
    // Check correct time format and split into components
    let times: any = time.toString().match(/^([01]\d|2[0-3])?$/) || [time];

    if (times.length > 1) {
        // If time format correct
        times = times.slice(1); // Remove full string match value
        times[2] = +times[0] < 12 ? "AM" : "PM"; // Set AM/PM
        times[0] = `${+times[0] % 12 || 12}`; // Adjust hours
    }
    return times.join(""); // return adjusted time or original string
}

export function getNowEquivalent(date: moment.Moment): moment.Moment {
    return getComparableScheduleMoment(date);
}

function getComparableScheduleMoment(
    date?: string | Date | moment.Moment
): moment.Moment {
    const parsedDate = moment.isMoment(date)
        ? date.clone()
        : moment(getStringFromScheduleDate(date));
    return moment()
        .day(parsedDate.day())
        .hour(parsedDate.hour())
        .minute(parsedDate.minute())
        .second(parsedDate.second())
        .millisecond(0);
}

function normalizeComparableRange(start: moment.Moment, end: moment.Moment) {
    const normalizedStart = start.clone();
    const normalizedEnd = end.clone();

    while (normalizedEnd.isBefore(normalizedStart)) {
        normalizedEnd.add(1, "week");
    }

    return {
        start: normalizedStart,
        end: normalizedEnd,
    };
}

function hasComparableScheduleOverlap(
    firstStart: moment.Moment,
    firstEnd: moment.Moment,
    secondStart: moment.Moment,
    secondEnd: moment.Moment
) {
    const firstRange = normalizeComparableRange(firstStart, firstEnd);
    const secondRange = normalizeComparableRange(secondStart, secondEnd);
    const secondRangeCandidates = [
        secondRange,
        {
            start: secondRange.start.clone().subtract(1, "week"),
            end: secondRange.end.clone().subtract(1, "week"),
        },
        {
            start: secondRange.start.clone().add(1, "week"),
            end: secondRange.end.clone().add(1, "week"),
        },
    ];

    return secondRangeCandidates.some(
        ({ start, end }) =>
            firstRange.start.isBefore(end) && start.isBefore(firstRange.end)
    );
}

export function toTitleCase(str: string) {
    return str?.replace(/\w\S*/g, function (txt) {
        return txt?.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
}

export function formatLegacyPhoneNumber(phoneNumber: string) {
    const digits = phoneNumber.replace(/\D/g, "");
    const nationalDigits =
        digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
    const formattedNumber = `+1${nationalDigits}`;

    if (nationalDigits.length !== 10) {
        return phoneNumber;
    }

    return isPossiblePhoneNumber(formattedNumber) ? formattedNumber : phoneNumber;
}

export function displayPhoneNumber(phoneNumber: string) {
    return formatPhoneNumber(formatLegacyPhoneNumber(phoneNumber)) || phoneNumber;
}

export function getPhoneNumberSearchVariations(phoneNumber: string) {
    const normalizedPhoneNumber = formatLegacyPhoneNumber(phoneNumber);
    const digits = normalizedPhoneNumber.replace(/\D/g, "");

    if (!normalizedPhoneNumber.startsWith("+1") || digits.length !== 11) {
        return [phoneNumber];
    }

    const nationalDigits = digits.slice(1);
    const areaCode = nationalDigits.slice(0, 3);
    const prefix = nationalDigits.slice(3, 6);
    const lineNumber = nationalDigits.slice(6);

    return Array.from(
        new Set([
            normalizedPhoneNumber,
            nationalDigits,
            `(${areaCode}) ${prefix}-${lineNumber}`,
            `${areaCode}-${prefix}-${lineNumber}`,
        ])
    );
}

/**
 *  It is expected to return a negative value if first argument is less than
 * second argument, zero if they're equal and a positive value otherwise
 */
export function sortSchedules(a: ISchedule[]) {
    return sortBy(a, (d) => moment(d.start as string).hour());
}

export function getStringVariations(value: string) {
    return [value, value.toLowerCase(), value.toUpperCase(), toTitleCase(value)];
}

export function isValidEmail(email: string) {
    const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
}

export function copyToClipboard(text: string) {
    var dummy = document.createElement("textarea");
    document.body.appendChild(dummy);
    dummy.value = text;
    dummy.select();
    document.execCommand("copy");
    document.body.removeChild(dummy);
}

export function copyArrayToString(arr: any[]) {
    copyToClipboard(arr.map((v) => v || "-").join(" "));
}

export function getClinicianName(clinician: IClinician) {
    const prefix = [ClinicianCredential.PHD, ClinicianCredential.PSYD].includes(
        clinician.credential
    )
        ? "Dr. "
        : "";
    return `${prefix} ${clinician.firstname} ${clinician.lastname}`.trim();
}

export function getStateFromAddress(state: string) {
    try {
        const byComma = state.split(",");
        const bySpace = state.split(" ");
        const toUse = byComma.length > 1 ? byComma : bySpace;
        return toUse.slice(-1).join(" ").replace(",", "").trim();
    } catch {}
}

/**
 * Maps a stored state abbreviation or name to the display name from {@link STATES}.
 * If there is no match (e.g. non-US text), returns the trimmed input.
 */
export function getStateDisplayName(state: string | undefined | null): string {
    if (state == null || !String(state).trim()) {
        return "";
    }
    const t = String(state).trim();
    const byAbbrv = STATES.find((s) => s.abbrv.toUpperCase() === t.toUpperCase());
    if (byAbbrv) {
        return byAbbrv.name;
    }
    const byName = STATES.find((s) => s.name.toLowerCase() === t.toLowerCase());
    if (byName) {
        return byName.name;
    }
    return t;
}

export function getNextXWeeksMoment(
    currentMoment: moment.Moment,
    duration: number,
    day: number
) {
    const today = moment();
    const noOfWeeks = today.diff(currentMoment, "weeks") + 1;
    let nextWeekDay = currentMoment;
    for (let i = 1; i < Math.round(noOfWeeks / duration); i++) {
        nextWeekDay = nextWeekDay.add(duration, "weeks");
    }
    if (nextWeekDay.isBefore(today)) {
        nextWeekDay = nextWeekDay.add(duration, "weeks");
    }
    nextWeekDay = nextWeekDay.day(day);
    return nextWeekDay;
}

// function to determine available slots that match office windows
export const findAvailableSlotsThatMatchOfficeWindow = (
    availabilityWindows: IAvailabilityWindow[],
    location: string,
    office: string,
    startTime: DateType,
    endTime: DateType
) => {
    const startWindow = getComparableScheduleMoment(startTime);
    const endWindow = getComparableScheduleMoment(endTime);
    const result: IAvailabilitySchedule[] = availabilityWindows.filter(
        (item: IAvailabilitySchedule) => {
            const start = getComparableScheduleMoment(item.start);
            const end = getComparableScheduleMoment(item.end);
            return (
                isBookingWithinTimeRange(start, end, startWindow, endWindow) &&
                item.location === location &&
                item.office === office
            );
        }
    );
    return result;
};

export function getLengthOfClinicianAvaliability(arr: IAvailabilityWindow[]) {
    // should return the length of array of availabilty windows with recurrenceRule not equal to One_Off or One_Off not before today and not reserved for anyone
    const filteredArray = Array.from(arr).filter(
        (d) =>
            (d.recurrenceRule === RecurrenceRule.ONE_OFF &&
                !moment(
                    (d.starting_date || d.start) && (d.end || d.ending_date)
                ).isBefore(moment()) &&
                !Boolean(d.reservedFor)) ||
            (!Boolean(d.reservedFor) && d.recurrenceRule !== RecurrenceRule.ONE_OFF)
    );
    return filteredArray.length;
}

export function cloneClient(
    client: IClient,
    props?: { [key: string]: any }
): IClient {
    let clonedClient = cloneDeep(client);
    if (props && Object.keys(props).length > 0) {
        clonedClient = assignIn(clonedClient, props);
    }
    return clonedClient;
}

export function convertToSafeString(unsafeStr: string, spacer = " "): string {
    const safeSplitRegex = /[/]|[.]|[ ]|[_]/gim;
    return unsafeStr.split(safeSplitRegex).join(spacer);
}

export function sortStringsWithNumberEndings(unsortedArray: string[]): string[] {
    try {
        return unsortedArray.sort((a, b) => {
            const aNum = parseInt(a.replace(/\D/g, ""));
            const bNum = parseInt(b.replace(/\D/g, ""));
            return aNum - bNum;
        });
    } catch (ex) {
        console.log(ex);
        return unsortedArray;
    }
}

export function getClinicianActualAvailableSlots(arr: IAvailabilityWindow[]) {
    // should return an array of availabilty windows with recurrenceRule not equal to One_Off or One_Off not before today and not reserved for anyone
    const filteredArray = Array.from(arr).filter(
        (d) =>
            (d.recurrenceRule === RecurrenceRule.ONE_OFF &&
                !moment(
                    (d.starting_date || d.start) && (d.end || d.ending_date)
                ).isBefore(moment()) &&
                !Boolean(d.reservedFor)) ||
            (!Boolean(d.reservedFor) && d.recurrenceRule !== RecurrenceRule.ONE_OFF)
    );
    return filteredArray;
}

// get the available start dates for booking appointments based on the selected appointment to hold and clinicians availability
export function getAvailableStartDates(
    clinicianToBook: IClinician,
    selectedApptToHoldId: string,
    selectedAppointmentToHoldDate: Date,
    _availableApptStartingDates: moment.Moment[]
) {
    const selectedApptToHold = clinicianToBook.available.find(
        (a) => a.id === selectedApptToHoldId
    );
    const selectedStart = selectedApptToHold?.start;
    selectedAppointmentToHoldDate = selectedStart
        ? new Date(selectedStart)
        : new Date();
    if (selectedAppointmentToHoldDate.getTime() < new Date().getTime()) {
        selectedAppointmentToHoldDate = new Date();
    }

    if (!selectedApptToHold) {
        throw Error("selectedApptToHold is not expected to be null/undefined");
    }

    let selectedStartMoment = moment(selectedStart);
    const today = moment();
    const weekDuration = getNumberOfWeeks(selectedApptToHold.recurrenceRule);
    while (selectedStartMoment.isBefore(today) && weekDuration > 0) {
        selectedStartMoment = getNextXWeeksMoment(
            selectedStartMoment.clone().add(weekDuration, "week"),
            getNumberOfWeeks(selectedApptToHold.recurrenceRule),
            selectedStartMoment.day()
        );
    }
    if (weekDuration !== 0) {
        _availableApptStartingDates = [1, 2, 3, 4]
            .map((wk) => wk * weekDuration)
            .map((wk) => {
                return getNextXWeeksMoment(
                    selectedStartMoment.clone().add(wk, "week"),
                    getNumberOfWeeks(selectedApptToHold.recurrenceRule),
                    selectedStartMoment.day()
                );
            });

        _availableApptStartingDates.splice(0, 0, selectedStartMoment);
        _availableApptStartingDates = orderBy(
            _availableApptStartingDates,
            (d) => d.toISOString(),
            "asc"
        );

        _availableApptStartingDates = uniqBy(_availableApptStartingDates, (d) =>
            d.format(DateFormats.DAY_MONTH_YEAR_FORMAT)
        ).filter((a) => {
            const isBeforeEndDate = selectedApptToHold.ending_date
                ? moment(selectedApptToHold.ending_date).isAfter(a)
                : true;
            const isNotVacationPeriod = !clinicianToBook.unavailable.some((e) =>
                a.isBetween(moment(e.start), moment(e.end), "day", "[]")
            );
            return (
                a.diff(today, "weeks") < MAX_APPT_DATE_DIFF_WEEKS &&
                a.isAfter(today, "days") &&
                isBeforeEndDate &&
                isNotVacationPeriod
            );
        });
    } else {
        _availableApptStartingDates = [moment(selectedStartMoment)];
    }

    return { selectedAppointmentToHoldDate, _availableApptStartingDates };
}

// check if request has conflict with logged in clinician schedule including remote windows
export const hasConflictingAvailabilityWithCliniciansRemoteSchedules = async (
    officeWindows: IOfficeWindow[],
    startDate: string,
    endDate: string
) => {
    const scheduleWindowStart = getComparableScheduleMoment(startDate);
    const scheduleWindowEnd = getComparableScheduleMoment(endDate);
    const hasConflict = officeWindows?.find((a) => {
        const availableWindowStart = getComparableScheduleMoment(a.start);
        const availableWindowEnd = getComparableScheduleMoment(a.end);
        const overlappingTime = hasComparableScheduleOverlap(
            availableWindowStart,
            availableWindowEnd,
            scheduleWindowStart,
            scheduleWindowEnd
        );
        const isSameBooking = overlappingTime;
        if (isSameBooking) {
            // Check if the conflict has an ending_date
            if (a.ending_date) {
                const isAfterEndDate = moment(
                    getStringFromScheduleDate(startDate)
                ).isAfter(moment(getStringFromScheduleDate(a.ending_date)));
                if (!isAfterEndDate) {
                    return true;
                }
            } else {
                return true;
            }
        }
        return false;
    });
    return hasConflict;
};

//validate date
export function isValidISODate(dateString) {
    const date = new Date(dateString);
    return date.toISOString() === dateString;
}
// function to check if the new schedule to be created is conflicting with an existing schedule
export const hasConflictingAvailability = (
    existingSchedules: IOfficeWindowSchedule[],
    startDate: string,
    endDate: string,
    office: string,
    location: string,
    loggedInUserEmail: string
) => {
    const scheduleWindowStart = getComparableScheduleMoment(startDate);
    const scheduleWindowEnd = getComparableScheduleMoment(endDate);
    const hasConflict = existingSchedules?.find((a) => {
        const availableWindowStart = getComparableScheduleMoment(a.start);
        const availableWindowEnd = getComparableScheduleMoment(a.end);
        const overlappingTime = hasComparableScheduleOverlap(
            availableWindowStart,
            availableWindowEnd,
            scheduleWindowStart,
            scheduleWindowEnd
        );
        const isSameBooking =
            overlappingTime && a.office === office && a.location === location;
        if (isSameBooking) {
            // Check if the conflict has an ending_date
            if (a.ending_date) {
                const isAfterEndDate = moment(
                    getStringFromScheduleDate(startDate)
                ).isAfter(moment(getStringFromScheduleDate(a.ending_date)));
                if (!isAfterEndDate) {
                    return true;
                }
            } else {
                return true;
            }
        }
        return false;
    });
    return hasConflict;
};

const isBookingWithinTimeRange = (
    scheduleStart: moment.Moment,
    scheduleEnd: moment.Moment,
    availableStart: moment.Moment,
    availableEnd: moment.Moment
) => {
    const availableRange = normalizeComparableRange(availableStart, availableEnd);
    const scheduleRange = normalizeComparableRange(scheduleStart, scheduleEnd);
    const scheduleCandidates = [
        scheduleRange,
        {
            start: scheduleRange.start.clone().add(1, "week"),
            end: scheduleRange.end.clone().add(1, "week"),
        },
    ];

    return scheduleCandidates.some(
        ({ start, end }) =>
            start.isSameOrAfter(availableRange.start) &&
            end.isSameOrBefore(availableRange.end)
    );
};

// this function is used to check if the schedule about to be requested is within an office window created by an office manager
export const isWithinOfficeWindow = (
    officeWindows: OfficeMangerOfficeWindow[],
    startDate: string,
    endDate: string,
    office: string,
    location: string
) => {
    const scheduleWindowStart = getComparableScheduleMoment(startDate);
    const scheduleWindowEnd = getComparableScheduleMoment(endDate);
    const matchingOfficeWindow = officeWindows.find((a) => {
        const availableWindowStart = getComparableScheduleMoment(a.start);
        const availableWindowEnd = getComparableScheduleMoment(a.end);

        const isSameBooking = a.office === office && a.location === location;

        // Check if the booking is within the time range
        const isBookingWithinRange = isBookingWithinTimeRange(
            scheduleWindowStart,
            scheduleWindowEnd,
            availableWindowStart,
            availableWindowEnd
        );

        if (isSameBooking && isBookingWithinRange && !a.blocked) {
            return true;
        }
        // if an office is blocked check when the blocking ends
        if (
            (a.start_date && a.end_date && a.blocked) ||
            (a.start_date && a.blocked)
        ) {
            const blockStartDate = moment(a.start_date).startOf("day");
            const blockEndDate = moment(a.end_date).endOf("day");

            const startDateMoment = moment(startDate).startOf("day");
            if (
                (startDateMoment.isBefore(blockStartDate) ||
                    startDateMoment.isAfter(blockEndDate)) &&
                isSameBooking &&
                isBookingWithinRange
            ) {
                return true;
            }
        }
        return false;
    });

    return !!matchingOfficeWindow;
};

export const addSpaceToString = (input: string) => {
    return input.replace(/([a-z])([A-Z])/g, "$1 $2");
};

// Define a function to find matching available schedules for all clinicians in the matched clinicians details array
function findMatchingAvailableSchedulesForClinician(
    availableSchedules: IAvailabilityWindow[],
    clinicianSchedules: IOfficeWindow[]
): IAvailabilityWindow[] {
    const matchingSchedules: IAvailabilityWindow[] = [];

    // Iterate through the available schedules for the clinician
    for (const availabilityWindow of availableSchedules) {
        const start = getComparableScheduleMoment(availabilityWindow.start);
        const end = getComparableScheduleMoment(availabilityWindow.end);

        // Check if the availabilityWindow matches the criteria in clinicianSchedules
        const isMatch = clinicianSchedules.some((schedule) => {
            const startWindow = getComparableScheduleMoment(schedule.start);
            const endWindow = getComparableScheduleMoment(schedule.end);
            return (
                schedule.location === availabilityWindow.location &&
                schedule.office === availabilityWindow.office &&
                isBookingWithinTimeRange(start, end, startWindow, endWindow)
            );
        });
        if (isMatch) {
            // If it's a match, add it to the clinician's matching schedules array
            matchingSchedules.push(availabilityWindow);
        }
    }

    return matchingSchedules;
}

export function findMatchingSchedulesForClinicians(
    matchedCliniciansDetails: { available: IAvailabilityWindow[]; email: string }[],
    matchingClinicians: {
        clinicianId: string;
        schedules: IOfficeWindow[];
    }[]
) {
    const matchingSchedules: {
        clinicianId: string;
        schedules: IAvailabilityWindow[];
    }[] = [];

    // Iterate through the response array
    for (const clinicianData of matchedCliniciansDetails) {
        const { available, email } = clinicianData;
        // Find the matching clinician from matchingClinicians based on email
        const matchingClinician = matchingClinicians.find(
            (clinician) => clinician.clinicianId === email
        );
        if (matchingClinician) {
            const clinicianMatchingSchedules =
                findMatchingAvailableSchedulesForClinician(
                    available,
                    matchingClinician.schedules
                );

            // Add the clinician's matching schedules to the result array
            if (clinicianMatchingSchedules.length > 0) {
                matchingSchedules.push({
                    clinicianId: email,
                    schedules: clinicianMatchingSchedules,
                });
            }
        }
    }
    return matchingSchedules;
}

// Define a function to check if any of the clinicians schedule is the same with the schedule about to be deleted by the office manager
export function isSameBooking(
    schedule: IOfficeWindow,
    officeWindowToDelete: IOfficeWindow
) {
    const availableWindowStart = getComparableScheduleMoment(
        officeWindowToDelete.start
    );
    const scheduleWindowStart = getComparableScheduleMoment(schedule.start);
    const availableWindowEnd = getComparableScheduleMoment(officeWindowToDelete.end);
    const scheduleWindowEnd = getComparableScheduleMoment(schedule.end);
    return (
        isBookingWithinTimeRange(
            scheduleWindowStart,
            scheduleWindowEnd,
            availableWindowStart,
            availableWindowEnd
        ) &&
        schedule.office === officeWindowToDelete.office &&
        schedule.location === officeWindowToDelete.location
    );
}

export const getAvailableRequestStartDates = (draftRequestOfficeWindow: {
    start_date: Date;
    recurrence: string;
}) => {
    let selectedRequestToHoldDate = new Date();
    selectedRequestToHoldDate = draftRequestOfficeWindow.start_date
        ? new Date(draftRequestOfficeWindow.start_date)
        : new Date();
    if (selectedRequestToHoldDate.getTime() < new Date().getTime()) {
        selectedRequestToHoldDate = new Date();
    }
    let selectedStartMoment = moment(draftRequestOfficeWindow.start_date);

    const today = moment();
    const weekDuration = getNumberOfWeeks(draftRequestOfficeWindow.recurrence);
    while (selectedStartMoment.isBefore(today) && weekDuration > 0) {
        selectedStartMoment = getNextXWeeksMoment(
            selectedStartMoment.clone().add(weekDuration, "week"),
            getNumberOfWeeks(draftRequestOfficeWindow.recurrence),
            selectedStartMoment.day()
        );
    }
    let startingDates = [moment()];

    if (weekDuration !== 0) {
        startingDates = [1, 2, 3, 4]
            .map((wk) => wk * weekDuration)
            .map((wk) => {
                return getNextXWeeksMoment(
                    selectedStartMoment.clone().add(wk, "week"),
                    getNumberOfWeeks(draftRequestOfficeWindow.recurrence),
                    selectedStartMoment.day()
                );
            });

        const MAX_APPT_DATE_DIFF_WEEKS = 4;
        startingDates.splice(0, 0, selectedStartMoment);
        startingDates = orderBy(startingDates, (d) => d.toISOString(), "asc");

        startingDates = uniqBy(startingDates, (d) =>
            d.format(DateFormats.DAY_MONTH_YEAR_FORMAT)
        ).filter((a) => {
            return (
                a.diff(today, "weeks") < MAX_APPT_DATE_DIFF_WEEKS &&
                a.isAfter(today, "days")
            );
        });
        return startingDates;
    } else {
        return (startingDates = [moment(selectedStartMoment)]);
    }
};

export const parseTime = (time) => {
    return moment(time, "h:mm A");
};

export const groupAvailableTimesIntoWindows = (times: string[], windowHours = 4) => {
    const windows: { fromTime: string; toTime: string }[] = [];
    let startIndex = 0;

    while (startIndex + windowHours <= times.length) {
        const start = parseTime(times[startIndex]);
        let hasContiguousSlots = true;
        for (let offset = 0; offset < windowHours; offset++) {
            const slot = parseTime(times[startIndex + offset]);
            if (slot.isBefore(start)) slot.add(1, "day");
            if (slot.diff(start, "hours", true) !== offset) {
                hasContiguousSlots = false;
                break;
            }
        }

        if (hasContiguousSlots) {
            windows.push({
                fromTime: start.format("h:mm A"),
                toTime: start.clone().add(windowHours, "hours").format("h:mm A"),
            });
            startIndex += windowHours;
        } else {
            startIndex += 1;
        }
    }

    return windows;
};

// function to get occupied slots from times in a day
export const isOccupied = (startTime, endTime, occupiedSlots) => {
    return occupiedSlots?.some((occupiedSlot) => {
        const startSlotTime = parseTime(occupiedSlot.start_time);
        const endSlotTime = parseTime(occupiedSlot.end_time);

        return (
            (startSlotTime.isSameOrAfter(startTime) &&
                startSlotTime.isBefore(endTime)) ||
            (endSlotTime.isAfter(startTime) &&
                endSlotTime.isSameOrBefore(endTime)) ||
            (startSlotTime.isBefore(startTime) && endSlotTime.isAfter(endTime))
        );
    });
};

// get all time values in a day
export const getAllTimesInDay = (intervalMinutes: number) => {
    const times: string[] = [];
    let start: moment.Moment = moment("00:00", "HH:mm");

    while (start.isBefore(moment("24:00", "HH:mm"))) {
        times.push(start.format("h:mm A"));
        start.add(intervalMinutes, "minutes");
    }
    return times;
};

// function to convert date to iso string without utc
export const handleFormatDatePickerValue = (inputValue: string) => {
    const date = new Date(inputValue);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

// function to get times that has been set to be booked by office managers
export const getAvailableTimesFromOfficeWindowsSetByOfficeManagers = (
    location: string,
    office: string,
    day: string,
    officeWindows: OfficeMangerOfficeWindow[],
    startDate: moment.Moment
) => {
    if (location !== "" && office !== "" && day !== "") {
        const filteredWindows = officeWindows.filter((window) => {
            const isMatchingLocation = window.location === location;
            const isMatchingOffice = window.office === office;
            const isMatchingDay =
                moment(window.start).format(DateFormats.DAY) === day;

            // check for if the window was blocked and when it will no longer be blocked
            const blockStartDate = moment(window.start_date).startOf("day");
            const blockEndDate = moment(window.end_date).endOf("day");
            const startDateMoment = moment(startDate).startOf("day");

            // general criteria
            const generalCriteria =
                isMatchingLocation && isMatchingOffice && isMatchingDay;
            return (
                (generalCriteria && !window.blocked) ||
                (generalCriteria &&
                    (startDateMoment.isBefore(blockStartDate) ||
                        startDateMoment.isAfter(blockEndDate)))
            );
        });

        // Map the filtered results to return start_time and end_time
        const availableTimes = filteredWindows.map((window) => ({
            start_time: moment(window.start).format(DateFormats.HOUR_MIN_FORMAT),
            end_time: moment(window.end).format(DateFormats.HOUR_MIN_FORMAT),
        }));
        return availableTimes;
    }
    // Return an empty array if conditions are not met
    return [];
};

// Normalize office number for box styling
export function normalizeOfficeNumber(officeNumber: string): string {
    // Remove "Office " prefix if present
    let normalized = officeNumber.replace(/^Office\s+/i, "");

    // Remove leading zeros and convert to number, then back to string
    // This handles "01", "02", "03" -> "1", "2", "3"
    const num = parseInt(normalized, 10);
    if (!isNaN(num)) {
        return num.toString();
    }

    // If it's not a number, return as is
    return normalized;
}

// check for times that are being used by the logged in clinicians for the selected day
export const getTimesUsedByClinicianForTheSelectedDay = (
    loggedInClinicianWindows: IOfficeWindow[],
    day: string
) => {
    const filteredWindows = loggedInClinicianWindows?.filter((window) => {
        return moment(window.start).format(DateFormats.DAY) === day;
    });
    // Map the filtered results to return start_time and end_time
    const availableTimes = filteredWindows?.map((window) => ({
        start_time: moment(window.start).format(DateFormats.HOUR_MIN_FORMAT),
        end_time: moment(window.end).format(DateFormats.HOUR_MIN_FORMAT),
    }));
    return availableTimes || [];
};

// check for times that are being used by other clinicians for that location
export const getTimesUsedByOtherClincians = (
    existingSchedules: IOfficeWindowSchedule[],
    location: string,
    office: string,
    day: string
) => {
    const filteredWindows = existingSchedules?.filter((window) => {
        return (
            window.location === location &&
            window.office === office &&
            moment(window.start).format(DateFormats.DAY) === day
        );
    });
    // Map the filtered results to return start_time and end_time
    const availableTimes = filteredWindows?.map((window) => ({
        start_time: moment(window.start).format(DateFormats.HOUR_MIN_FORMAT),
        end_time: moment(window.end).format(DateFormats.HOUR_MIN_FORMAT),
    }));
    return availableTimes || [];
};

// this is the function to get time slots that clinician can book for the day
export const findAvailableTimeSlotsClinicianCanBook = (occupiedSlots) => {
    const availableSlots: string[] = [];
    const allTimesInDay = getAllTimesInDay(60);
    allTimesInDay.forEach((slot, index, array) => {
        const slotStartTime = parseTime(slot);
        const slotEndTime = parseTime(array[index + 1] || "23:59 PM");

        const isSlotOccupied = isOccupied(slotStartTime, slotEndTime, occupiedSlots);
        if (!isSlotOccupied) {
            availableSlots.push(slot);
        }
    });
    return availableSlots;
};

// helper function for suggestAvailableOfficesAndTimes to check is times are within window
const isWithinCreatedOfficeWindow = (timeSlot, officeWindows) => {
    const timeMoment = moment(timeSlot, "h:mma");
    return officeWindows.some((window) => {
        const startTime = moment(window.start_time, "h:mma");
        const endTime = moment(window.end_time, "h:mma");
        return (
            timeMoment.isSameOrAfter(startTime) &&
            timeMoment.clone().add(1, "hour").isSameOrBefore(endTime)
        );
    });
};

// function to get suggested offices and times by location and day
export const suggestAvailableOfficesAndTimes = ({
    availableOffices,
    location,
    day,
    loggedInClinicianWindows,
    existingSchedules,
    heldWindows,
    officeWindows,
    fromDate,
}: SuggestTimeAndOffice) => {
    const availableOfficesAndTimes = {};
    const allTimesUsed = [] as TimesUsed[];

    // Iterate through offices in the selected location
    const officesInLocation = availableOffices[location] || [];
    officesInLocation.forEach(async (office) => {
        // Availability checks for each office
        const timesUsedByOtherClinicians = getTimesUsedByOtherClincians(
            existingSchedules,
            location,
            office,
            day
        );
        allTimesUsed.push(...timesUsedByOtherClinicians);

        // check for offices that are held for a reason
        const heldTimes = getTimesUsedByOtherClincians(
            heldWindows,
            location,
            office,
            day
        );
        allTimesUsed.push(...heldTimes);

        // get times used by the logged in clinician for that day this also includes remote offices
        const timesUsedByClinician = getTimesUsedByClinicianForTheSelectedDay(
            loggedInClinicianWindows,
            day
        );

        allTimesUsed.push(...timesUsedByClinician);

        // this contains all the times that are not available for that day and location
        const allUnAvailableTimes = [
            ...timesUsedByOtherClinicians.map((time) => ({ ...time, office })),
            ...heldTimes.map((time) => ({ ...time, office })),
            ...timesUsedByClinician.map((time) => ({ ...time })),
        ];

        // this is the time slots that are remaining for the clinician to book after removing the unavailable times
        const timeSlotsAvailable =
            findAvailableTimeSlotsClinicianCanBook(allUnAvailableTimes);

        // now we check the list of office windows created by office managers and only return times within these windows
        const officeWindowsAvailableToBook =
            getAvailableTimesFromOfficeWindowsSetByOfficeManagers(
                location,
                office,
                day,
                officeWindows,
                fromDate
            );

        // Filter time slots available within any of the office windows
        const filteredTimes = timeSlotsAvailable.filter((timeSlot) => {
            return isWithinCreatedOfficeWindow(
                timeSlot,
                officeWindowsAvailableToBook
            );
        });

        // Store results for each office
        availableOfficesAndTimes[office] = filteredTimes;
    });

    return {
        availableOfficesAndTimes,
        allTimesUsed,
    };
};

// function to get department label by practice id
export const findLabelByPracticeId = (
    practiceId: string,
    allDepartmentsAndServices: DepartmentCache
) => {
    for (const departmentKey in allDepartmentsAndServices) {
        const department = allDepartmentsAndServices[departmentKey];
        if (department.practice_id === practiceId) {
            return department.label;
        }
    }
    return null; // Return null if the practice_id is not found
};

export const findPracticeIdByLabel = (
    label: string,
    allDepartmentsAndServices: DepartmentCache
) => {
    for (const departmentKey in allDepartmentsAndServices) {
        const department = allDepartmentsAndServices[departmentKey];
        if (department.label === label) {
            return department.practice_id;
        }
    }
    return null;
};

//  merge client data correctly
export function mergeClientData(
    primary_client, // Parent Client (highest priority)
    existing_client_data, // Other client at different statuses (if any)
    existing_history_data, // Archived client Completed status
    incomplete_inquiry_data // Incomplete inquiry (if any)
) {
    const merged_notes = [
        String(incomplete_inquiry_data?.notes || ""),
        String(primary_client?.notes || ""),
        String(existing_client_data?.notes || ""),
        String(existing_history_data?.notes || ""),
    ]
        .filter(Boolean)
        .join("\n\n---\n\n");

    const mergedArrays = (...arrays) => {
        return Array.from(new Set(arrays.flat().filter(Boolean)));
    };

    return {
        ...primary_client,
        ...existing_history_data,
        ...existing_client_data,
        last_name:
            primary_client?.last_name || incomplete_inquiry_data?.last_name || "",
        first_name:
            primary_client?.first_name || incomplete_inquiry_data?.last_name || "",
        preferred_name:
            primary_client?.preferred_name ||
            incomplete_inquiry_data?.preferred_name ||
            "",
        pronouns:
            primary_client?.pronouns || incomplete_inquiry_data?.pronouns || "",
        assigned_to:
            primary_client?.assigned_to ||
            existing_client_data?.assigned_to ||
            existing_history_data?.assigned_to ||
            incomplete_inquiry_data?.assigned_to ||
            "",
        inquiry_method:
            existing_history_data?.inquiry_method ||
            existing_client_data?.inquiry_method ||
            primary_client?.inquiry_method ||
            incomplete_inquiry_data?.inquiry_method ||
            "",
        phone: primary_client?.phone_no || incomplete_inquiry_data?.phone_no || "",
        notes: merged_notes,
        date_of_birth:
            primary_client?.date_of_birth ||
            existing_client_data?.date_of_birth ||
            existing_history_data?.date_of_birth ||
            null,
        tracking_params: primary_client?.tracking_params || "",
        cookie_params: primary_client?.cookie_params || "",
        date_updated: new Date(),
        held_bookings: mergedArrays(
            primary_client?.held_bookings || [],
            existing_client_data?.held_bookings || [],
            existing_history_data?.held_bookings || []
        ),
        confirmed_bookings: mergedArrays(
            primary_client?.confirmed_bookings || [],
            existing_client_data?.confirmed_bookings || [],
            existing_history_data?.confirmed_bookings || []
        ),
        appointment_history: mergedArrays(
            primary_client?.appointment_history || [],
            existing_client_data?.appointment_history || [],
            existing_history_data?.appointment_history || []
        ),
        waitlist_clinician_ids:
            primary_client?.waitlist_clinician_ids ||
            existing_client_data?.waitlist_clinician_ids ||
            existing_history_data?.waitlist_clinician_ids ||
            [],
        waitlist_locations:
            primary_client?.waitlist_locations ||
            existing_client_data?.waitlist_locations ||
            existing_history_data?.waitlist_locations ||
            [],
        waitlist_time_categories:
            primary_client?.waitlist_time_categories ||
            existing_client_data?.waitlist_time_categories ||
            existing_history_data?.waitlist_time_categories ||
            [],
        waitlist_days:
            primary_client?.waitlist_days ||
            existing_client_data?.waitlist_days ||
            existing_history_data?.waitlist_days ||
            [],
        waitlist_wants_intern:
            primary_client?.waitlist_wants_intern ||
            existing_client_data?.waitlist_wants_intern ||
            existing_history_data?.waitlist_wants_intern ||
            false,
        complete_notes: primary_client?.complete_notes ||
            existing_client_data?.complete_notes ||
            existing_history_data?.complete_notes || {
                reason: "",
            },
    };
}

export { mergeClients } from "intake_shared/src/client_merge";

export const getCityFromAddress = (address: string): string => {
    if (!address) {
        return "";
    }

    const addressParts = address.split(",").map((part) => part.trim());
    // Return the last part as the city
    return addressParts.length > 1 ? addressParts[addressParts.length - 1] : "";
};

export const findFeeByPracticeId = (
    practiceId: string,
    allDepartmentsAndServices: DepartmentCache
) => {
    for (const departmentKey in allDepartmentsAndServices) {
        const department = allDepartmentsAndServices[departmentKey];
        if (department.practice_id === practiceId) {
            return department.fee_type;
        }
    }
    return null; // Return null if the practice_id is not found
};

export const showMoneyInAppFormat = (amount: string | number) => {
    // Convert amount to valid number
    const convertedAmount = Number(amount);

    // Check if converted value is a valid number
    const isValidNumber =
        !Number.isNaN(convertedAmount) && Number.isFinite(convertedAmount);

    // Format amount to desired style
    if (isValidNumber) {
        return new Intl.NumberFormat("us-EN", {
            style: "currency",
            currency: "USD",
        })
            .format(convertedAmount)
            .replace("US", "");
    }
    return "--";
};

export const getConfirmedEmailToSend = (
    confirmation_type: "client_and_clinician_confirm" | "clinician_confirm_only",
    updated_client: IClient,
    updated_clinician: IClinician,
    client_appointment: IClientBooking,
    allDepartmentAndServices: DepartmentCache,
    signature: SignatureData,
    supervisor_name: string,
    requested_service: string,
    service_delivery_option: string,
    department: string,
    locationAddress: string,
    triggered_from: "template" | "system" = "system",
    form: ConsentForm,
    intakeSignature: SignatureData
) => {
    if (confirmation_type === "client_and_clinician_confirm") {
        if (triggered_from === "system") {
            sendAppointmentConfirmedEmails(
                updated_client,
                updated_clinician,
                client_appointment,
                allDepartmentAndServices,
                intakeSignature,
                locationAddress
            );
        }
        const isRemote =
            isSameString(requested_service, "Teletherapy") ||
            isSameString(service_delivery_option, "remote") ||
            isSameString(service_delivery_option, "telemed");

        const isMentalHealthAssessment =
            isSameString(
                requested_service,
                "Mental Health Assessment: Fully In-Person"
            ) ||
            isSameString(
                requested_service,
                "Mental Health Assessment: Partial In-Person"
            ) ||
            isSameString(requested_service, "Mental Health Assessment: Virtual");

        const isKetamineAssistedPsychotherapy =
            isSameString(requested_service, "Ketamine Assisted Psychotherapy") ||
            isSameString(
                requested_service,
                "Ketamine Assisted Psychotherapy (Evaluation)"
            ) ||
            isSameString(
                requested_service,
                "Ketamine Assisted Psychotherapy (Therapy Intake)"
            );

        if (isRemote && department === "HT") {
            sendAppointmentConfirmedHTTeletherapyClientEmails(
                updated_client,
                updated_clinician,
                client_appointment,
                signature,
                department,
                allDepartmentAndServices,
                locationAddress,
                form,
                supervisor_name
            );
        }

        if (isRemote && department === "PH") {
            sendAppointmentConfirmedPHTeletherapyClientEmails(
                updated_client,
                updated_clinician,
                client_appointment,
                signature,
                department,
                allDepartmentAndServices,
                locationAddress,
                form,
                supervisor_name
            );
        }

        if (
            isRemote &&
            department === "CHP" &&
            updated_clinician.credential !== "PNP" &&
            requested_service !== "Nutrition Counseling" &&
            requested_service !== "Career Assessment" &&
            requested_service !== "Extern Therapy" &&
            !isMentalHealthAssessment &&
            !isKetamineAssistedPsychotherapy
        ) {
            sendAppointmentConfirmedCHPTeletherapyClientEmails(
                updated_client,
                updated_clinician,
                client_appointment,
                signature,
                department,
                allDepartmentAndServices,
                locationAddress,
                form,
                supervisor_name
            );
        }

        if (!isRemote && department === "HT") {
            sendAppointmentConfirmedHTInPersonClientEmails(
                updated_client,
                updated_clinician,
                client_appointment,
                signature,
                department,
                allDepartmentAndServices,
                locationAddress,
                form,
                supervisor_name
            );
        }
        if (!isRemote && department === "PH") {
            sendAppointmentConfirmedPHInPersonClientEmails(
                updated_client,
                updated_clinician,
                client_appointment,
                signature,
                department,
                allDepartmentAndServices,
                locationAddress,
                form,
                supervisor_name
            );
        }
        if (
            !isRemote &&
            department === "CHP" &&
            requested_service !== "Nutrition Counseling" &&
            requested_service !== "Career Assessment" &&
            requested_service !== "Extern Therapy" &&
            !isMentalHealthAssessment &&
            !isKetamineAssistedPsychotherapy
        ) {
            sendAppointmentConfirmedCHPInPersonClientEmails(
                updated_client,
                updated_clinician,
                client_appointment,
                signature,
                department,
                allDepartmentAndServices,
                locationAddress,
                form,
                supervisor_name
            );
        }
        if (requested_service === "Extern Therapy" && department === "CHP") {
            sendAppointmentConfirmedExternTherapyClientEmails(
                updated_client,
                updated_clinician,
                client_appointment,
                signature,
                department,
                allDepartmentAndServices,
                locationAddress,
                form,
                supervisor_name
            );
        }
        if (isMentalHealthAssessment && department === "CHP") {
            sendAppointmentConfirmedTestingClientEmails(
                updated_client,
                updated_clinician,
                client_appointment,
                signature,
                department,
                allDepartmentAndServices,
                locationAddress,
                form,
                supervisor_name
            );
        }

        if (isKetamineAssistedPsychotherapy && department === "CHP") {
            sendAppointmentConfirmedKAPClientEmails(
                updated_client,
                updated_clinician,
                client_appointment,
                signature,
                department,
                allDepartmentAndServices,
                locationAddress,
                form,
                supervisor_name
            );
        }

        if (requested_service === "Career Assessment" && department === "CHP") {
            sendAppointmentConfirmedCareerAssessmentClientEmails(
                updated_client,
                updated_clinician,
                client_appointment,
                signature,
                department,
                allDepartmentAndServices,
                locationAddress,
                form,
                supervisor_name
            );
        }
        if (requested_service === "Nutrition Counseling" && department === "CHP") {
            sendAppointmentConfirmedNutritionCounselingClientEmails(
                updated_client,
                updated_clinician,
                client_appointment,
                signature,
                department,
                allDepartmentAndServices,
                locationAddress,
                form,
                supervisor_name
            );
        }

        if (
            isRemote &&
            updated_clinician.credential === "PNP" &&
            department === "CHP" &&
            requested_service !== "Nutrition Counseling" &&
            requested_service !== "Career Assessment" &&
            requested_service !== "Extern Therapy" &&
            !isMentalHealthAssessment &&
            !isKetamineAssistedPsychotherapy
        ) {
            sendAppointmentConfirmedPnpTherapyClientEmails(
                updated_client,
                updated_clinician,
                client_appointment,
                signature,
                department,
                allDepartmentAndServices,
                locationAddress,
                form,
                supervisor_name
            );
        }
    } else if (confirmation_type === "clinician_confirm_only") {
        sendAppointmentConfirmedEmails(
            updated_client,
            updated_clinician,
            client_appointment,
            allDepartmentAndServices,
            intakeSignature,
            locationAddress
        );
    }
};

export const validateFeeFormat = (feeValue: string): boolean => {
    const trimmed = feeValue.trim();
    if (!trimmed) return true;

    // Allow explicit NOT ACCEPTED marker (e.g., for INN fee boxes)
    if (trimmed === "NOT ACCEPTED") {
        return true;
    }

    const patterns = [
        // 1. 80x5; $15 TBD (with $) - space before TBD is optional
        /^\d+x\d+;\s*\$\d+\s*TBD$/,
        // 2. 180x14; 130TBD (without $) - space before TBD is optional
        /^\d+x\d+;\s*\d+\s*TBD$/,
        // 3. 120TBD - space before TBD is optional
        /^\d+\s*TBD$/,
        // 4. 25
        /^\d+$/,
        // 5. 120x7; $90 (with $)
        /^\d+x\d+;\s*\$\d+$/,
        // 6. 120x7; 90 (without $)
        /^\d+x\d+;\s*\d+$/,
        // 7. Psych Fee formats:
        //    a) 455x1; 255 (evaluation + ongoing) - accepts whole numbers or decimals
        /^\d+(?:\.\d+)?x\d+;\s*\d+(?:\.\d+)?$/,
        //    b) 655x1; 455 UDM, then 80 (evaluation + UDM + ongoing) - accepts whole numbers or decimals
        /^\d+(?:\.\d+)?x\d+;\s*\d+(?:\.\d+)?\s+UDM,\s*then\s+\d+(?:\.\d+)?$/,
        //    c) 165.76x2; 160.89 UDM, then 32.18 (evaluation with multiple sessions + UDM + ongoing) - accepts mixed whole/decimal
        /^\d+(?:\.\d+)?x\d+;\s*\d+(?:\.\d+)?\s+UDM,\s*then\s+\d+(?:\.\d+)?$/,
        // 8. Single psych fee (whole number or decimal) - e.g., 16 or 16.09
        /^\d+(?:\.\d+)?$/,
        // 9. UDM + ongoing format (no evaluation) - e.g., 452 UDM, then 22 or 4.52 UDM, then 22.0
        /^\d+(?:\.\d+)?\s+UDM,\s*then\s+\d+(?:\.\d+)?$/,
        // 10. KAP structured fee with evaluation, intake, initial KAP block, then ongoing - e.g., 200; 100; 1000X2 THEN 200
        /^\d+(?:\.\d+)?\s*;\s*\d+(?:\.\d+)?\s*;\s*\d+(?:\.\d+)?x\d+\s+then\s+\d+(?:\.\d+)?$/i,
        // 11. KAP structured fee with evaluation, intake, and single KAP per-session rate - e.g., 200; 100; 1000
        /^\d+(?:\.\d+)?\s*;\s*\d+(?:\.\d+)?\s*;\s*\d+(?:\.\d+)?$/,
    ];

    return patterns.some((pattern) => pattern.test(trimmed));
};

export const validateFees = (
    inputFee = "",
    label = "",
    department_fee_type = ""
): string => formatFeeCalculation(inputFee, label, department_fee_type);
